import { compareOrganisationNames } from "@/utils/organisationSort";

import { SidebarProvider } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useCrmAccess } from "@/hooks/use-crm-access";
import { Navigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCommercialStatus } from "@/hooks/use-commercial-status";
import { CommercialStatusBadges } from "@/components/common/CommercialStatusBadges";
import { Search, Globe, MapPin, Users, Eye, EyeOff, Filter, Calendar, User, Edit, Trash2, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FixedPageHeader } from "@/components/ui/fixed-page-header";
import { PAGE_HEADERS } from "@/constants/page-headers";
import { useUpdateCompany, useCompanies, useDeleteCompany } from "@/hooks/use-companies";
import { useContacts } from "@/hooks/use-contacts";
import { useRoleBasedAccess } from "@/hooks/use-role-based-access";
import { CompanyModal } from "@/components/crm/CompanyModal";
import { OrganizationEditModal } from "@/components/crm/OrganizationEditModal";
import { OrganizationDetailModal } from "@/components/crm/OrganizationDetailModal";
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const CrmCompanies = () => {
  const { data: hasCrmAccess, isLoading } = useCrmAccess();
  const { session } = useAuth();
  const { isPartner, partnerId, isSystemAdmin, isInternalStaff } = useRoleBasedAccess();

  // Get all organizations and contacts (we'll filter them based on role)
  const allOrganizations = useCompanies();
  const allContacts = useContacts();

  console.log('🔍 [CrmCompanies] Current partner status:', isPartner);
  console.log('🔍 [CrmCompanies] Current partner ID:', partnerId);
  console.log('🔍 [CrmCompanies] All organizations count:', allOrganizations.data?.length || 0);
  
  // Filter organizations based on user role with safety checks
  const organizationsData = isPartner && partnerId
    ? (allOrganizations.data || []).filter(org => {
        // For partners, show organizations they own or have contacts for
        const isDirectlyOwned = org.external_owner_id === partnerId;
        const hasContact = (allContacts.data || []).some(contact => 
          contact.external_owner_id === partnerId && contact.organization_id === org.id
        );
        const shouldShow = isDirectlyOwned || hasContact;
        
        console.log(`🔍 [CrmCompanies] Org ${org.name} (${org.id}):`, {
          external_owner_id: org.external_owner_id,
          partnerId,
          isDirectlyOwned,
          hasContact,
          shouldShow
        });
        
        return shouldShow;
      })
    : (allOrganizations.data || []);

  console.log('🔍 [CrmCompanies] Filtered organizations count:', organizationsData.length);

  const companies = organizationsData;
  const contactsData = allContacts.data || [];
  const isLoading_data = allOrganizations.isLoading || allContacts.isLoading;

  const updateCompany = useUpdateCompany();
  const deleteCompany = useDeleteCompany();
  const [searchTerm, setSearchTerm] = useState("");
  const [relationshipFilter, setRelationshipFilter] = useState<string>("all");
  const [commercialFilter, setCommercialFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("name_asc");
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const { getOrgFlags } = useCommercialStatus();

  const toggleVisibility = (company: any, e: React.MouseEvent) => {
    e.stopPropagation();
    const newVisibility = company.visibility === 'public' ? 'private' : 'public';
    updateCompany.mutate({
      id: company.id,
      data: { ...company, visibility: newVisibility }
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!hasCrmAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleEditCompany = (company: any) => {
    setSelectedCompany(company);
    setEditModalOpen(true);
  };

  const handleViewCompany = (company: any) => {
    setSelectedCompany(company);
    setDetailModalOpen(true);
  };

  const handleDeleteCompany = (company: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Are you sure you want to delete ${company.name}? This action cannot be undone.`)) {
      deleteCompany.mutate(company.id);
    }
  };

  // Filter and sort companies
  const filteredCompanies = companies
    .filter(company => {
      // Search filter
      const searchMatch = searchTerm === "" || 
        company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (company.industry && company.industry.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (company.country && company.country.toLowerCase().includes(searchTerm.toLowerCase()));
      
      // Relationship filter
      const relationshipMatch = relationshipFilter === "all" || 
        company.relationship_status === relationshipFilter;

      // Commercial status filter (Pilot Pack / Free Camera)
      const flags = getOrgFlags(company.id);
      const commercialMatch =
        commercialFilter === "all" ||
        (commercialFilter === "pilot" && flags.isPilot) ||
        (commercialFilter === "commercial" && !flags.isPilot);

      return searchMatch && relationshipMatch && commercialMatch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return compareOrganisationNames(a.name, b.name);
        case "name_desc":
          return compareOrganisationNames(b.name, a.name);
        case "created_at_desc":
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        case "updated_at_desc":
          return new Date(b.updated_at || 0).getTime() - new Date(a.updated_at || 0).getTime();
        default:
          return 0;
      }
    });

  const getRelationshipBadgeColor = (status: string) => {
    switch (status) {
      case 'Customer':
        return 'bg-green-100 text-green-800';
      case 'Prospect':
        return 'bg-blue-100 text-blue-800';
      case 'Partner':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getCompanyAdmins = (companyId: string) => {
    return contactsData.filter(contact => 
      contact.organization_id === companyId && 
      contact.job_title && (
        contact.job_title.toLowerCase().includes('admin') ||
        contact.job_title.toLowerCase().includes('director') ||
        contact.job_title.toLowerCase().includes('ceo') ||
        contact.job_title.toLowerCase().includes('managing director') ||
        contact.job_title.toLowerCase().includes('manager')
      )
    );
  };

  const getOrganizationOwner = (organization: any) => {
    // Check for internal owner (staff)
    if (organization.internal_owner) {
      return {
        name: `${organization.internal_owner.first_name} ${organization.internal_owner.last_name}`,
        type: 'Staff'
      };
    }
    
    // Check for external owner (partner)
    if (organization.external_owner) {
      return {
        name: `${organization.external_owner.first_name} ${organization.external_owner.last_name}`,
        type: 'Partner'
      };
    }
    
    return null;
  };

  if (isLoading_data) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full bg-background">
          <AppSidebar />
          <main className="flex-1">
            <div className="p-8">
              <div className="text-slate-600">Loading organisations...</div>
            </div>
          </main>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full bg-background">
        <AppSidebar />
        <main className="flex-1">
          <FixedPageHeader {...PAGE_HEADERS.crmCompanies} actions={<CompanyModal />} />
          <div className="px-4 md:px-8 py-6 md:py-8 space-y-8" style={{ paddingTop: "8.5rem" }}>

            {/* Summary Card */}
            <div className="grid grid-cols-1 md:grid-cols-1 gap-6 max-w-md">
              <Card className="bg-gradient-to-br from-white to-slate-50 border-slate-200 rounded-xl shadow-sm">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-semibold text-slate-700">Total Organisations</CardTitle>
                    <div className="bg-slate-100 p-2 rounded-lg">
                      <Building2 className="h-5 w-5 text-slate-600" />
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-3xl font-bold text-slate-800">{companies.length}</div>
                </CardContent>
              </Card>
            </div>

            {/* Search and Filters */}
            <div className="flex items-center gap-4 flex-wrap">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-500 h-4 w-4" />
                <Input
                  placeholder="Search organisations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 border-slate-300 focus:border-teal-500 focus:ring-teal-500 rounded-lg bg-white"
                />
              </div>
              
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <Select value={relationshipFilter} onValueChange={setRelationshipFilter}>
                  <SelectTrigger className="w-[150px] bg-white border-slate-300">
                    <SelectValue placeholder="Filter by" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organisations</SelectItem>
                    <SelectItem value="Prospect">Prospects</SelectItem>
                    <SelectItem value="Customer">Customers</SelectItem>
                    <SelectItem value="Partner">Partners</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(isSystemAdmin || isInternalStaff) && (
                <Select value={commercialFilter} onValueChange={setCommercialFilter}>
                  <SelectTrigger className="w-[170px] bg-white border-slate-300">
                    <SelectValue placeholder="Commercial status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Ownership Types</SelectItem>
                    <SelectItem value="pilot">Pilot Pack</SelectItem>
                    <SelectItem value="commercial">Commercial Only</SelectItem>
                  </SelectContent>
                </Select>
              )}
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] bg-white border-slate-300">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Name A-Z</SelectItem>
                  <SelectItem value="name_desc">Name Z-A</SelectItem>
                  <SelectItem value="created_at_desc">Recently Added</SelectItem>
                  <SelectItem value="updated_at_desc">Recently Updated</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Organizations Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCompanies.map((company) => {
                const admins = getCompanyAdmins(company.id);
                const owner = getOrganizationOwner(company);
                return (
                  <Card 
                    key={company.id}
                    className="group hover:shadow-lg transition-all duration-300 overflow-hidden hover:-translate-y-1 cursor-pointer bg-white"
                    onClick={() => handleViewCompany(company)}
                  >
                    {/* Header without teal gradient */}
                    <div className="h-1 bg-gradient-to-r from-slate-200 to-slate-300 shadow-sm"></div>
                    
                     <CardHeader className="pb-4 bg-white relative">
                       <div className="flex items-start justify-between">
                         <div className="flex items-center gap-4 flex-1">
                            {company.logo_url ? (
                              <div className="h-14 w-14 border shadow-sm ring-1 rounded-lg overflow-hidden bg-white" style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}>
                                <img
                                  src={company.logo_url}
                                  alt={company.name}
                                  className="w-full h-full object-cover"
                                />
                              </div>
                            ) : (
                              <div className="h-14 w-14 border shadow-sm ring-1 rounded-lg bg-white flex items-center justify-center" style={{ borderColor: "black", boxShadow: "0 0 0 1px black" }}>
                                <Building2 className="h-7 w-7 text-black" />
                              </div>
                            )}
                           <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-lg text-slate-900 group-hover:text-slate-700 transition-colors duration-200 truncate">
                                {company.name}
                              </h3>
                             {company.company_id && (
                               <p className="text-sm font-medium text-slate-600 truncate mt-0.5">{company.company_id}</p>
                             )}
                           </div>
                         </div>
                       </div>
                       
                       {/* Edit and Delete icons that appear on hover */}
                       <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-1">
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-8 w-8 p-0 hover:bg-slate-100 hover:text-slate-700 text-slate-600"
                           onClick={(e) => {
                             e.stopPropagation();
                             handleEditCompany(company);
                           }}
                           title="Edit organisation"
                         >
                           <Edit className="h-4 w-4" />
                         </Button>
                         <Button
                           variant="ghost"
                           size="sm"
                           className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-700 text-slate-600"
                           onClick={(e) => handleDeleteCompany(company, e)}
                           title="Delete organisation"
                         >
                           <Trash2 className="h-4 w-4" />
                         </Button>
                       </div>
                     </CardHeader>

                     <CardContent className="pt-0 pb-5">
                       {/* Status badges moved to top of content */}
                       <div className="flex justify-end gap-2 mb-4">
                         {(isSystemAdmin || isInternalStaff) && <CommercialStatusBadges flags={getOrgFlags(company.id)} className="mr-auto" />}
                         <Badge 
                           variant="outline" 
                           className={`text-xs flex items-center gap-1 cursor-pointer hover:bg-slate-100 transition-colors ${
                             company.visibility === 'public' 
                               ? 'border-green-200 bg-green-50 text-green-700' 
                               : 'border-slate-200 bg-slate-50 text-slate-600'
                           }`}
                           onClick={(e) => toggleVisibility(company, e)}
                           title={`Click to make ${company.visibility === 'public' ? 'private' : 'public'}`}
                         >
                           {company.visibility === 'public' ? (
                             <>
                               <Eye className="h-3 w-3" />
                               Public
                             </>
                           ) : (
                             <>
                               <EyeOff className="h-3 w-3" />
                               Private
                             </>
                           )}
                         </Badge>
                         <Badge 
                           className="text-xs font-semibold px-3 py-1 rounded-full shadow-sm text-black"
                           style={{ backgroundColor: "white" }}
                         >
                           {company.relationship_status}
                         </Badge>
                       </div>
                      {/* Meta information */}
                      <div className="flex items-center justify-between mb-5">
                        <div className="flex items-center gap-2 text-xs text-slate-500">
                          <Calendar className="h-3 w-3" />
                          <span>Created {new Date(company.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      {/* Two-column layout for company information */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-5">
                        {/* Left column - Primary info */}
                        <div className="space-y-3">
                          {company.industry && (
                            <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                              <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                                <Users className="h-4 w-4 text-black" />
                              </div>
                              <span className="truncate font-medium">{company.industry}</span>
                            </div>
                          )}
                          
                          {company.website && (
                            <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                              <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                                <Globe className="h-4 w-4 text-black" />
                              </div>
                              <span className="truncate font-medium">{company.website}</span>
                            </div>
                          )}
                        </div>

                        {/* Right column - Secondary info */}
                        <div className="space-y-3">
                          {company.size && (
                            <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                              <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                                <Users className="h-4 w-4 text-black" />
                              </div>
                              <span className="truncate font-medium">{company.size} employees</span>
                            </div>
                          )}

                          {company.country && (
                            <div className="flex items-center gap-3 text-sm text-slate-700 hover:text-slate-900 transition-colors duration-150 group/item">
                              <div className="flex items-center justify-center h-8 w-8 bg-slate-50 rounded-lg group-hover/item:bg-slate-100 transition-colors duration-150">
                                <MapPin className="h-4 w-4 text-black" />
                              </div>
                              <span className="truncate font-medium">{company.country}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Company Admin Section */}
                      {admins.length > 0 && (
                        <div className="py-3 border-t border-slate-100">
                          <div className="flex items-center gap-3 text-sm text-slate-600">
                            <Users className="h-4 w-4 text-slate-400" />
                            <div>
                              <span className="font-medium text-slate-700">Company Admin: </span>
                              <span>{admins.map(admin => `${admin.first_name} ${admin.last_name}`).join(', ')}</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Organization Owner Section */}
                      {owner && (
                        <div className={`py-3 ${admins.length > 0 ? '' : 'border-t border-slate-100'} mb-4`}>
                          <div className="flex items-center gap-3 text-sm text-slate-600">
                            <User className="h-4 w-4 text-slate-400" />
                            <div>
                              <span className="font-medium text-slate-700">Owner: </span>
                              <span>{owner.name}</span>
                              <Badge variant="outline" className="ml-2 text-xs">
                                {owner.type}
                              </Badge>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Enhanced action buttons */}
                      <div className="space-y-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewCompany(company);
                          }}
                          className="w-full bg-[#DFEF97] text-black hover:bg-[#DFEF97]/90 border-none transition-all duration-200 font-semibold"
                        >
                          <Building2 className="h-4 w-4 mr-2" />
                          View Details
                        </Button>
                      </div>
                     </CardContent>
                   </Card>
                );
              })}
            </div>

            {/* No results message */}
            {filteredCompanies.length === 0 && (
              <Card className="bg-white border-slate-200 rounded-xl shadow-sm">
                <CardContent className="p-8 text-center">
                  <Building2 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-800 mb-2">No Organisations Found</h3>
                  <p className="text-slate-600 mb-4">
                    {searchTerm ? "No organisations match your search criteria." : "Get started by adding your first organisation."}
                  </p>
                  {!searchTerm && <CompanyModal />}
                </CardContent>
              </Card>
            )}

            {/* Company Edit Modal */}
            {selectedCompany && editModalOpen && (
              <OrganizationEditModal
                organization={selectedCompany}
                open={editModalOpen}
                onOpenChange={(open) => {
                  setEditModalOpen(open);
                  if (!open) setSelectedCompany(null);
                }}
              />
            )}

            {/* Company Detail Modal */}
            {selectedCompany && detailModalOpen && (
              <OrganizationDetailModal
                organization={selectedCompany}
                open={detailModalOpen}
                onOpenChange={(open) => {
                  setDetailModalOpen(open);
                  if (!open) setSelectedCompany(null);
                }}
                onEdit={() => {
                  setDetailModalOpen(false);
                  handleEditCompany(selectedCompany);
                }}
              />
            )}
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
};

export default CrmCompanies;
